import { Injectable } from '@angular/core';
import { SettingService } from './setting.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ResponseService } from './response-service.service';
import Swal from 'sweetalert2';

interface CustomFile extends File {
  url?: string;
  path?: string;
  status?: boolean;
}

@Injectable()
export class FileService {

  service = 'file';
  productMS = 'microservice';

  public MAXIMUM_FILE_SIZE = 1e+7; // 10MB
  public MAXIMUM_VIDEO_SIZE = 1e+8; // 100MB


  constructor(
    private settingService: SettingService,
    private http: HttpClient,
    private rpSer: ResponseService,
  ) {
  }

  /**
   * Get files signed url
   * @param data
   */
  getFilesSignedUrls(data: any): Observable<any> {
    const requestUrl = this.settingService.getApiUrl(this.service, '/image-upload/v1');

    return this.http.post<any>(requestUrl, JSON.stringify(data), this.settingService.httpOptionWithToken());
  }

  compressImage(data: any): Observable<any> {
    const requestUrl = this.settingService.getUploadUrl(this.productMS, '/image/upload');

    return this.http.post<any>(requestUrl, data);
  }

  async uploadS3Sync(signedUrl: string, file: File, hideNotification = false) {
    let isSuccess = false;
    const options = {headers: {'Content-Type': file.type}};
    await this.http.put<any>(signedUrl, file, options)
      .toPromise()
      .then(() => {
        if (!hideNotification) {
          this.rpSer.showAddSuccess(file.name + ' success to upload');
          isSuccess = true;
        }
      })
      .catch(() => {
        if (!hideNotification) {
          this.rpSer.showAddError(file.name + ' failed to upload');
        }
      });
    return isSuccess;
  }

  popUpFileFromGoogleDrive(): void {
    // const words = translateService.data;
    const words = {
      'FILE_FROM_GOOGLE_DRIVE': 'File From Google Drive',
      'CANT_RETRIEVE_FILE_FROM_GOOGLE_DRIVE': 'Can\'t retrieve file from google drive',
    };

    Swal.fire({
      icon: 'error',
      title: `${words['FILE_FROM_GOOGLE_DRIVE'].toUpperCase()}`,
      html: `${words['CANT_RETRIEVE_FILE_FROM_GOOGLE_DRIVE']}`,
      showConfirmButton: false,
      showCancelButton: false,
    });
  }

  validationFileExtension(file: File, typesAllowed: Array<string>): Promise<any> {
    return new Promise((resolve) => {
      let isFileWrong = null;
      const filetype = file.type;
      let types: string[] = [];
      let match = true;
      const fileReader = new FileReader();
      fileReader.onloadend = (e) => {
        const arr = (new Uint8Array(e.target?.result as ArrayBuffer)).subarray(0, 4);
        let header = '';
        for(let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);  
        }
        
        if(header === '') {
          this.popUpFileFromGoogleDrive();
          resolve(true);
          return;
        }

        switch (header) {
          case '89504e47':
            types = ['image/png'];
            break;
          case '47494638':
            types = ['image/gif'];
            break;
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
            types = ['image/jpeg'];
            break;
          case '00018':
          case '00020':
            types = ['video/mp4'];
            break;
          case '52494646':
            types = ['video/avi', 'audio/wav'];
            break;
          case '00014':
            types = ['video/quicktime'];
            break;
          case '1a45dfa3':
            types = ['video/x-matroska'];
            break;
          case '001ba':
            types = ['video/mpeg'];
            break;
          case 'fffb824':
          case '4944334':
          case '4944333':
            types = ['audio/mpeg'];
            break;
          case '0001c':
            types = ['audio/x-m4a'];
            break;
          case 'fff15080':
            types = ['audio/vnd.dlna.adts'];
            break;
          case '25504446':
            types = ['application/pdf'];
            break;
          case '':
          case 'd0cf11e0':
            types = [
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation',
              'application/vnd.ms-powerpoint',
              'application/x-zip-compressed',
            ];
            break;
          case '44617461':
          case '6f6e4164':
          case '54756761':
          case '68747470':
            types = ['text/plain'];
            break;
          case '504b34':
            types = ['application/x-zip-compressed','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
            break;
        }

        if (file.type !== 'text/plain') {
          match = false;
          for (const type of types) {
            if (filetype === type) {
              match = true;
              break;
            }
          }
        } else {
          match = true;
        }

        if(!match) {
          isFileWrong = true;
        } else {
          isFileWrong = true;
          for(const typeAllowed of typesAllowed) {
            if(filetype === typeAllowed) {
              isFileWrong = false;
              break;
            }
          }
        }

        if (isFileWrong) {
          this.rpSer.showAddError(file.name + ' file type is not allowed. Allowed file types are: ' + typesAllowed.join(', '));
        }
    
        resolve(isFileWrong);
      };
      fileReader.readAsArrayBuffer(file);
    });
  }

  async validateFiles(files: File[], allowedFileType: Array<string>, fileSize = {file: this.MAXIMUM_FILE_SIZE, video: this.MAXIMUM_VIDEO_SIZE}) {
    for (const file of files) {
      const size = file.size;
      if(file.type.includes('video')) {
        if (fileSize.video && size > fileSize.video) {
          this.rpSer.showAddError(file.name + ' size is too large. Maximum size is');
          return false;
        }
      } else {
        if(fileSize.file && size > fileSize.file) {
          this.rpSer.showAddError(file.name + ' size is too large.');
          return false;
        }
      }

      const isFileWrong = await this.validationFileExtension(file, allowedFileType);
      if (isFileWrong) {
        return false;
      }

    }
    return true;
  }


  uploadFileS3(file: CustomFile, path: string, secure = false): Promise<any> {
    return new Promise( (resolve) => {
      const value = {
        name: file.name,
        type: file.type,
        path: path,
        secure: secure,
      };
       this.getFilesSignedUrls(value)
        .subscribe(async response => {
          // tslint:disable-next-line: no-console
          file['url'] = response.data.url.substring(0, response.data.url.search(/\?/));
          file['path'] = response.data.path;
          file['status'] = await this.uploadS3Sync(response.data.url, file);
          resolve(file);
        }, () => {
          file['status'] = false;
          resolve(file);
        });
    });
  }

  getFilenameFromContentDisposition(contentDisposition: string | null, defaultName = 'unknown'): string {
    if (!contentDisposition) {
      return defaultName;
    }

    const matches = /filename="(.+)"/.exec(contentDisposition);
    return matches ? matches[1] : defaultName;
  }
}
